"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";

import { authClient } from "../../lib/auth/client";
import { REQUIRE_EMAIL_VERIFICATION } from "../../lib/auth/policy";

export type AuthMode = "signin" | "signup";

/**
 * Sign in, sign up, and the "email me a link instead" path, in one form.
 *
 * They are one component because they are one decision for the student: the
 * fields are the same, and splitting them across pages means someone who
 * mistyped their address on sign-up has to navigate to fix it. The mode only
 * changes which endpoint is called and what the button says.
 *
 * Email and password are the default, and every field is on screen without
 * a toggle. The magic link was the default once: it is the path with nothing
 * to forget, but it is also the path that needs a mail transport, and with
 * SMTP unconfigured the prominent button on the front door sent nothing and
 * said nothing. Nobody could make an account. So the link is secondary now,
 * and `mailEnabled` hides it entirely when there is nothing to send with.
 */
export default function AuthForm({
  mode,
  googleEnabled,
  mailEnabled,
  next,
  heading: headingOverride,
  blurb: blurbOverride,
  onSwitch,
}: {
  mode: AuthMode;
  googleEnabled: boolean;
  mailEnabled: boolean;
  next?: string;
  /** Copy for places that open this form for a reason, such as a dialog. */
  heading?: string;
  blurb?: string;
  /** Present when the form sits in a dialog, where switching mode must not navigate. */
  onSwitch?: () => void;
}) {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  // Which path the submit button takes. Password unless the student asks
  // for a link, and a link is only offerable when mail can be sent.
  const [useLink, setUseLink] = useState(false);
  const usePassword = !(mailEnabled && useLink);
  const [busy, setBusy] = useState<
    null | "link" | "password" | "google" | "passkey"
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<null | "link" | "reset">(null);

  // Better Auth needs a path to return to. Only a same-site path is ever
  // accepted: taking ?next= straight from the query string would turn every
  // sign-in link into an open redirect.
  const callbackURL = safeNext(next) ?? "/library";

  async function emailLink() {
    setBusy("link");
    setError(null);
    const { error: err } = await authClient.signIn.magicLink({
      email,
      callbackURL,
    });
    setBusy(null);
    if (err) return setError(err.message ?? "That did not send. Try again.");
    setSent("link");
  }

  async function withPassword() {
    setBusy("password");
    setError(null);

    const { error: err } =
      mode === "signup"
        ? await authClient.signUp.email({
            email,
            password,
            name: name.trim() || email,
            callbackURL,
          })
        : await authClient.signIn.email({ email, password, callbackURL });

    setBusy(null);
    if (err) {
      // An unverified account is not a failed sign-in; it is a step the
      // student has not finished. Send them to the notice rather than
      // showing a red error they cannot act on.
      if (err.status === 403) return router.push("/verify-email");
      return setError(err.message ?? "That did not work. Check your details.");
    }
    // Only send them to the verification wall when the wall is real.
    // With the policy off, pushing there left a brand new account staring
    // at a page asking it to open a mail that was never sent.
    if (mode === "signup" && REQUIRE_EMAIL_VERIFICATION)
      return router.push("/verify-email");
    router.push(callbackURL);
  }

  async function withGoogle() {
    setBusy("google");
    setError(null);
    await authClient.signIn.social({ provider: "google", callbackURL });
  }

  async function withPasskey() {
    setBusy("passkey");
    setError(null);
    const result = await authClient.signIn.passkey();
    setBusy(null);
    if (result?.error) {
      return setError(result.error.message ?? "No passkey was used.");
    }
    router.push(callbackURL);
  }

  async function sendReset() {
    setBusy("link");
    setError(null);
    const { error: err } = await authClient.requestPasswordReset({
      email,
      redirectTo: "/reset-password",
    });
    setBusy(null);
    if (err) return setError(err.message ?? "That did not send. Try again.");
    setSent("reset");
  }

  if (sent) {
    return (
      <div className="rise-in">
        <h1 className="mb-3 font-heading text-[clamp(32px,4vw,48px)] leading-tight text-paper">
          Check your email
        </h1>
        <p className="font-body text-base leading-relaxed text-muted-dark">
          {sent === "link"
            ? "We sent a sign-in link to "
            : "We sent a password reset link to "}
          <span className="text-paper">{email}</span>.{" "}
          {sent === "link"
            ? "It works once and expires in fifteen minutes."
            : "It expires in an hour."}
        </p>
        <button
          type="button"
          onClick={() => setSent(null)}
          className="mt-8 font-body text-base text-blue underline underline-offset-4 hover:opacity-80"
        >
          Use a different address
        </button>
      </div>
    );
  }

  const heading =
    headingOverride ??
    (mode === "signup" ? "Create your account" : "Sign in to Syft");
  const blurb =
    blurbOverride ??
    (mode === "signup"
      ? "Your readings, rebuilds and Deep reads follow the account, on any device."
      : "Welcome back.");

  return (
    <div className="rise-in rounded-[2rem] bg-ink-alt p-8 sm:p-12">
      <h1 className="mb-3 font-heading text-[clamp(30px,3.6vw,42px)] leading-tight text-paper">
        {heading}
      </h1>
      <p className="mb-10 font-body text-base leading-relaxed text-muted-dark">
        {blurb}
      </p>

      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (usePassword) return void withPassword();
          void emailLink();
        }}
      >
        {mode === "signup" && usePassword && (
          <Field
            label="Name"
            id="name"
            type="text"
            autoComplete="name"
            value={name}
            onChange={setName}
            placeholder="What should we call you?"
          />
        )}

        <Field
          label="Email"
          id="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={setEmail}
          placeholder="you@example.com"
        />

        {usePassword && (
          <Field
            label="Password"
            id="password"
            type="password"
            autoComplete={
              mode === "signup" ? "new-password" : "current-password"
            }
            required
            minLength={8}
            value={password}
            onChange={setPassword}
            placeholder={mode === "signup" ? "At least eight characters" : ""}
          />
        )}

        {error && (
          <p role="alert" className="font-body text-base text-red">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy !== null || email.trim() === ""}
          className="mt-2 bg-blue px-6 py-4 font-body text-lg font-semibold text-blue-deep transition-opacity hover:opacity-90 disabled:opacity-40"
        >
          {busy === "link" || busy === "password"
            ? "One moment"
            : usePassword
              ? mode === "signup"
                ? "Create account"
                : "Sign in"
              : "Email me a sign-in link"}
        </button>
      </form>

      {/* Both of these need a mail transport. Offering either without one
          is how the front door came to have a button that did nothing. */}
      {mailEnabled && (
        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 font-body text-base">
          <button
            type="button"
            onClick={() => {
              setUseLink((current) => !current);
              setError(null);
            }}
            className="text-blue underline underline-offset-4 hover:opacity-80"
          >
            {usePassword ? "Email me a link instead" : "Use a password instead"}
          </button>
          {usePassword && mode === "signin" && (
            <button
              type="button"
              onClick={() => void sendReset()}
              disabled={email.trim() === ""}
              className="text-muted-dark underline underline-offset-4 hover:text-paper disabled:opacity-40"
            >
              Forgot your password?
            </button>
          )}
        </div>
      )}

      {/* Passkeys need no configuration, so this block always has at least
          one button in it; Google appears only when its keys are set. */}
      <div className="my-8 flex items-center gap-4" aria-hidden="true">
        <span className="h-px flex-1 bg-ink-rule" />
        <span className="font-body text-base font-semibold text-muted-dark">
          or
        </span>
        <span className="h-px flex-1 bg-ink-rule" />
      </div>

      <div className="flex flex-col gap-3">
        {googleEnabled && (
          <button
            type="button"
            onClick={() => void withGoogle()}
            disabled={busy !== null}
            className="border-2 border-paper/25 px-6 py-3.5 font-body text-base font-semibold text-paper transition-colors hover:border-blue hover:text-blue disabled:opacity-40"
          >
            Continue with Google
          </button>
        )}
        <button
          type="button"
          onClick={() => void withPasskey()}
          disabled={busy !== null}
          className="border-2 border-paper/25 px-6 py-3.5 font-body text-base font-semibold text-paper transition-colors hover:border-blue hover:text-blue disabled:opacity-40"
        >
          {busy === "passkey" ? "Waiting for your device" : "Use a passkey"}
        </button>
      </div>

      <p className="mt-10 font-body text-base text-muted-dark">
        {mode === "signup" ? "Already have an account? " : "New to Syft? "}
        {onSwitch ? (
          <button
            type="button"
            onClick={onSwitch}
            className="text-blue underline underline-offset-4 hover:opacity-80"
          >
            {mode === "signup" ? "Sign in" : "Create one"}
          </button>
        ) : (
          <Link
            href={mode === "signup" ? "/sign-in" : "/sign-up"}
            className="text-blue underline underline-offset-4 hover:opacity-80"
          >
            {mode === "signup" ? "Sign in" : "Create one"}
          </Link>
        )}
      </p>
    </div>
  );
}

function Field({
  label,
  id,
  value,
  onChange,
  ...input
}: {
  label: string;
  id: string;
  value: string;
  onChange: (value: string) => void;
} & Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "id"
>) {
  return (
    <label htmlFor={id} className="flex flex-col gap-2">
      <span className="font-body text-base font-semibold text-muted-dark">
        {label}
      </span>
      <input
        {...input}
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="border-2 border-transparent bg-ink px-5 py-3.5 font-body text-base text-paper placeholder:text-muted-dark/50 focus:border-blue"
      />
    </label>
  );
}

/**
 * Only same-site paths survive. Anything absolute, protocol-relative, or
 * otherwise off-site is dropped rather than sanitised, because a sign-in
 * redirect is exactly the thing worth phishing.
 */
function safeNext(next: string | undefined): string | null {
  if (!next) return null;
  if (!next.startsWith("/") || next.startsWith("//")) return null;
  return next;
}
